import { z } from 'zod';
import { queryBoolean } from '../api/query-boolean';

/**
 * The Forms service's public contract.
 *
 * A submission references a person by id and nothing more, and only when one
 * is supplied at all — an anonymous submission is a form's ordinary case, not
 * an edge one.
 */

export const FORM_STATUSES = ['DRAFT', 'PUBLISHED', 'ARCHIVED'] as const;
export type FormStatus = (typeof FORM_STATUSES)[number];

export const FORM_FIELD_TYPES = [
  'TEXT',
  'TEXTAREA',
  'NUMBER',
  'EMAIL',
  'PHONE',
  'DATE',
  'SELECT',
  'CHECKBOX',
  'RADIO',
] as const;
export type FormFieldType = (typeof FORM_FIELD_TYPES)[number];

// --- Field definitions --------------------------------------------------------

const OPTION_TYPES: readonly FormFieldType[] = ['SELECT', 'RADIO'];

export const formFieldSchema = z
  .object({
    id: z.string().trim().min(1).max(64),
    label: z.string().trim().min(1).max(200),
    type: z.enum(FORM_FIELD_TYPES),
    required: z.boolean().default(false),
    /** Required, and non-empty, exactly for SELECT and RADIO fields. */
    options: z.array(z.string().trim().min(1).max(200)).max(50).nullish(),
  })
  .refine((field) => !OPTION_TYPES.includes(field.type) || (field.options?.length ?? 0) > 0, {
    message: 'SELECT and RADIO fields need at least one option',
    path: ['options'],
  });
export type FormFieldDefinition = z.infer<typeof formFieldSchema>;

const formFieldsSchema = z
  .array(formFieldSchema)
  .max(100)
  .refine((fields) => new Set(fields.map((field) => field.id)).size === fields.length, {
    message: 'Field ids must be unique within a form',
  });

// --- Requests ---------------------------------------------------------------

const formTitleField = z.string().trim().min(1).max(150);

export const createFormRequestSchema = z.object({
  title: formTitleField,
  description: z.string().trim().max(2000).nullish(),
  campusId: z.uuid().nullish(),
  organizationId: z.uuid().nullish(),
  fields: formFieldsSchema.default([]),
});
export type CreateFormRequest = z.infer<typeof createFormRequestSchema>;

/**
 * Declared field by field rather than as `createFormRequestSchema.partial()`.
 *
 * `.partial()` keeps `.default()` values, so an empty PATCH would parse to a
 * body full of defaults and quietly overwrite real data with them.
 */
export const updateFormRequestSchema = z
  .object({
    title: formTitleField.optional(),
    description: z.string().trim().max(2000).nullish(),
    campusId: z.uuid().nullish(),
    organizationId: z.uuid().nullish(),
    fields: formFieldsSchema.optional(),
  })
  .refine((value) => Object.values(value).some((field) => field !== undefined), {
    message: 'At least one field must be supplied',
  });
export type UpdateFormRequest = z.infer<typeof updateFormRequestSchema>;

export const archiveFormRequestSchema = z.object({
  reason: z.string().trim().min(1).max(500),
});
export type ArchiveFormRequest = z.infer<typeof archiveFormRequestSchema>;

export const submitFormRequestSchema = z.object({
  personId: z.uuid().nullish(),
  answers: z.record(z.string(), z.unknown()),
});
export type SubmitFormRequest = z.infer<typeof submitFormRequestSchema>;

export const listFormsQuerySchema = z.object({
  cursor: z.string().nullish(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
  search: z.string().trim().max(200).nullish(),
  status: z.enum(FORM_STATUSES).nullish(),
  campusId: z.uuid().nullish(),
  includeArchived: queryBoolean().default(false),
});
export type ListFormsQuery = z.infer<typeof listFormsQuerySchema>;

export const listSubmissionsQuerySchema = z.object({
  cursor: z.string().nullish(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
});
export type ListSubmissionsQuery = z.infer<typeof listSubmissionsQuerySchema>;

// --- Responses --------------------------------------------------------------

export interface FormSummaryResponse {
  readonly id: string;
  readonly title: string;
  readonly status: FormStatus;
  readonly campusId: string | null;
  readonly fieldCount: number;
  readonly submissionCount: number;
  readonly archivedAt: string | null;
}

export interface FormDetailResponse extends FormSummaryResponse {
  readonly description: string | null;
  readonly organizationId: string | null;
  readonly fields: readonly FormFieldDefinition[];
  readonly createdAt: string;
  readonly updatedAt: string;
}

/**
 * `displayName` is resolved through PeopleReadPort at read time rather than
 * copied into the submission row, the same choice Groups and Events make for
 * their own person references. Null for an anonymous submission.
 */
export interface FormSubmissionResponse {
  readonly id: string;
  readonly personId: string | null;
  readonly displayName: string | null;
  readonly answers: Readonly<Record<string, unknown>>;
  readonly submittedAt: string;
}
